import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import { useIsAdmin } from '../../../contexts/PermissionsContext';

const formatPrice = (cents) => {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString();
};

const getRegistrationStatus = (event) => {
  if (event.registrationOpen) return { label: 'Open', color: 'success' };
  if (event.registrationOpenDate && new Date(event.registrationOpenDate) > new Date()) {
    return { label: 'Scheduled', color: 'info' };
  }
  return { label: 'Closed', color: 'default' };
};

const EventListPage = () => {
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await unifiedApi.getEvents();
      setEvents(data || []);
    } catch {
      setError('Failed to load events.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await unifiedApi.deleteEvent(deleteTarget.id);
      setDeleteTarget(null);
      fetchEvents();
    } catch (err) {
      const msg = err?.message || '';
      if (msg.includes('paid or active pending')) {
        setError('Cannot delete an event with paid or active pending registrations.');
      } else {
        setError('Failed to delete event.');
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4">Events</Typography>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<RefreshIcon />} onClick={fetchEvents} disabled={loading}>
              Refresh
            </Button>
            {isAdmin && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                component={Link}
                to="/admin/events/new"
              >
                Create Event
              </Button>
            )}
          </Stack>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : events.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No events yet. Create your first event to get started.
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Capacity</TableCell>
                  <TableCell>Registration</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((event) => {
                  const regStatus = getRegistrationStatus(event);
                  return (
                    <TableRow
                      key={event.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/admin/events/${event.id}/registrations`)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {event.name}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatPrice(event.priceInCents)}</TableCell>
                      <TableCell>
                        {event.registrationCount}/{event.totalSlots}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={regStatus.label}
                          size="small"
                          color={regStatus.color}
                          clickable={false}
                          onClick={() => {}}
                        />
                      </TableCell>
                      <TableCell>{formatDate(event.createdAt)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                          {isAdmin && (
                            <>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  component={Link}
                                  to={`/admin/events/${event.id}/edit`}
                                  aria-label={`Edit ${event.name}`}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setDeleteTarget(event)}
                                  aria-label={`Delete ${event.name}`}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        {deleteTarget && (
          <>
            <DialogTitle>Delete Event</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete <strong>{deleteTarget.name}</strong>?
                This action cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button
                onClick={handleDelete}
                color="error"
                variant="contained"
                disabled={deleting}
                startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </AdminLayout>
  );
};

export default EventListPage;
